import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { EventAvailableRounded, GridOnRounded } from '@mui/icons-material';
import { attendanceApi, type AttendanceMatrixDto } from '../../../lib/api/attendance.api';
import { errorText } from '../../../lib/error-text';
import {
  useTenantStore,
  useTenantTimeZone,
  useTenantDateFormat,
} from '../../../stores/tenant.store';
import { formatDateOnly, todayInZoneWeb } from '@dosfilos/shared';
import { jornadaParaAbrir, jornadasPendientes } from '../../../lib/attendance-pending';
import { AttendanceMatrixDialog, AttendanceRosterDialog } from './AttendanceDialogs';

// ─── Tomar la asistencia ─────────────────────────────────────────────────────
//
// POR QUÉ ESTA PESTAÑA EXISTE, medido en producción: de 23 jornadas dictadas,
// 15 tienen asistencia — dos docentes al 100 % y dos casi en cero. Uno de ellos
// la tomó UNA VEZ y no volvió.
//
// ESO NO ES «NO LA ENCUENTRO», es «la encontré y no valía repetirlo». El
// marcado ya era rápido —el diálogo da por presente a quien no se toca, y hay
// botón de «todos presentes»—; lo caro era LLEGAR: entrar al cronograma,
// encontrar el módulo, encontrar el día, pulsar un ícono pequeño. Tres
// decisiones antes de tocar nada.
//
// ASÍ QUE ESTA PANTALLA NO REDISEÑA EL MARCADO: acorta el camino. Lo pendiente
// arriba, la jornada que toca abierta de entrada, y la matriz debajo — que es
// otra pregunta, «cómo va el curso», y por eso no va primero.
//
// LA ASISTENCIA SIGUE ESTANDO EN EL CRONOGRAMA. Ahí le sirve a quien planifica,
// y el control de modalidad vive en la misma fila. Esta pestaña es el camino
// del que sólo quiere marcar; no se le quitó nada a nadie.

export function AttendanceTab({ sectionId }: { sectionId: string }): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const tz = useTenantTimeZone();
  const fmt = useTenantDateFormat();

  const [matriz, setMatriz] = useState<AttendanceMatrixDto | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [abierta, setAbierta] = useState<{ moduleId: string; date: string } | null>(null);
  const [verMatriz, setVerMatriz] = useState(false);
  // Sólo se abre sola UNA VEZ por sección: si el docente cierra el diálogo, no
  // se lo volvemos a poner delante en cada recarga.
  const [yaAbrioSola, setYaAbrioSola] = useState(false);

  // EL DÍA, EN LA ZONA DEL SEMINARIO. Con el huso del navegador, un docente
  // conectado desde otro país vería la clase de hoy como pendiente de ayer.
  const hoy = todayInZoneWeb(tz);

  const cargar = useCallback(() => {
    if (!currentTenantId || !sectionId) return;
    setCargando(true);
    setError(null);
    attendanceApi
      .getSectionAttendanceMatrix({ tenantId: currentTenantId, sectionId })
      .then(setMatriz)
      .catch((e) => setError(errorText(e)))
      .finally(() => setCargando(false));
  }, [currentTenantId, sectionId]);

  useEffect(cargar, [cargar]);
  useEffect(() => {
    setYaAbrioSola(false);
    setMatriz(null);
  }, [sectionId]);

  const pendientes = useMemo(() => jornadasPendientes(matriz, hoy), [matriz, hoy]);

  // ABRE EN LA QUE TOCA. Es la mitad del arreglo: el docente entra y está
  // marcando, no buscando.
  useEffect(() => {
    if (cargando || yaAbrioSola || abierta) return;
    const j = jornadaParaAbrir(pendientes, hoy);
    if (j) {
      setAbierta(j);
      setYaAbrioSola(true);
    }
  }, [cargando, yaAbrioSola, abierta, pendientes, hoy]);

  if (cargando && !matriz) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress size={28} />
      </Stack>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;

  const dictadas = (matriz?.days ?? []).filter((d) => d.date <= hoy).length;
  const tomadas = dictadas - pendientes.length;

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {t('attendanceTab.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('attendanceTab.subtitle', { taken: tomadas, total: dictadas })}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<GridOnRounded />}
          onClick={() => setVerMatriz(true)}
          disabled={(matriz?.days.length ?? 0) === 0}
        >
          {t('attendanceTab.matrix')}
        </Button>
      </Stack>

      {pendientes.length === 0 ? (
        <Alert severity="success" icon={<EventAvailableRounded />}>
          {dictadas === 0 ? t('attendanceTab.noClassesYet') : t('attendanceTab.allDone')}
        </Alert>
      ) : (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          {/* LA MÁS ANTIGUA PRIMERO: es el orden en que conviene saldarlas, y
              deja arriba la que más tiempo lleva sin tomarse. */}
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {t('attendanceTab.pendingTitle', { count: pendientes.length })}
          </Typography>
          <Stack spacing={1} mt={1}>
            {pendientes.map((j) => (
              <Stack
                key={`${j.moduleId}-${j.date}`}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                useFlexGap
                sx={{ py: 0.5 }}
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" fontWeight={600}>
                    {formatDateOnly(j.date, fmt)}
                  </Typography>
                  {j.date === hoy && (
                    <Chip
                      size="small"
                      color="primary"
                      label={t('attendanceTab.today')}
                      sx={{ height: 20 }}
                    />
                  )}
                </Stack>
                <Button size="small" variant="contained" onClick={() => setAbierta(j)}>
                  {t('attendanceTab.take')}
                </Button>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {abierta && currentTenantId && (
        <AttendanceRosterDialog
          tenantId={currentTenantId}
          moduleId={abierta.moduleId}
          date={abierta.date}
          onClose={() => setAbierta(null)}
          onSaved={() => {
            setAbierta(null);
            cargar();
          }}
        />
      )}
      {verMatriz && currentTenantId && (
        <AttendanceMatrixDialog
          tenantId={currentTenantId}
          sectionId={sectionId}
          onClose={() => setVerMatriz(false)}
        />
      )}
    </Stack>
  );
}
